import { useState, useEffect, useRef } from 'react';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuTransition, setMenuTransition] = useState('');
  const [activeSection, setActiveSection] = useState('hero'); // 默认选中首页
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // 滚动监听（用于控制滚动后的样式变化和活动section检测）
  useEffect(() => {
    const sections = ['hero', 'about', 'cases', 'news', 'contact'];
    
    const handleScroll = () => {
      // 更新滚动状态
      setIsScrolled(window.scrollY > 100);
      
      // 检测当前可见的section
      const currentSection = sections.find(sectionId => {
        const element = document.getElementById(sectionId);
        if (!element) return false;
        
        const rect = element.getBoundingClientRect();
        // 设置一个偏移量，让section在视口中显示一定部分时就认为是活动的
        return rect.top <= 150 && rect.bottom >= 150;
      });
      
      // 如果找到当前可见的section，则更新活动状态
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // 初始化时执行一次，确保初始状态正确
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 移动端菜单过渡逻辑保留
  useEffect(() => {
    if (isMobileMenuOpen) {
      setMenuTransition('opacity 300ms ease');
      setTimeout(() => {
        setMenuTransition('opacity 300ms ease, max-height 300ms ease');
      }, 50);
    } else {
      setMenuTransition('max-height 300ms ease');
      setTimeout(() => {
        setMenuTransition('opacity 300ms ease, max-height 300ms ease');
      }, 50);
    }
  }, [isMobileMenuOpen]);

  // 点击外部关闭菜单的功能
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 确保菜单是打开的
      if (!isMobileMenuOpen) return;
      
      // 检查点击目标是否在菜单区域内
      const target = event.target as Node;
      const isClickInMenu = menuRef.current?.contains(target);
      const isClickOnMenuButton = menuButtonRef.current?.contains(target);
      
      // 如果点击在菜单区域外且不是点击菜单按钮，则关闭菜单
      if (!isClickInMenu && !isClickOnMenuButton) {
        setIsMobileMenuOpen(false);
      }
    };

    // 添加点击事件监听
    document.addEventListener('mousedown', handleClickOutside);
    
    // 清理函数
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // 获取header的高度作为偏移量，避免标题被固定头部遮挡
      const headerHeight = document.querySelector('header')?.offsetHeight || 100;
      
      // 使用window.scrollTo而不是element.scrollIntoView，以便可以添加偏移量
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementTop - headerHeight - 20, // 额外减去20px的间距
        behavior: 'smooth'
      });
      
      // 立即更新活动section状态，提供即时反馈
      setActiveSection(sectionId);
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white backdrop-blur-lg border-b border-gray-200 shadow-sm' : 'bg-[#4677e0] backdrop-blur-lg border-b border-white/20'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-[clamp(0.75rem,1.5vw,1rem)]">
        <div className="flex items-center justify-between">
          {/* Logo + 公司名称 */}
          <div className="flex items-center space-x-[clamp(0.5rem,1vw,0.75rem)]">
            <img
              src="https://static.readdy.ai/image/09017fd5fb6872f2f1d290b9c874ed1a/eb09dfbd45336b6ef38e5b52a1f917db.png"
              alt="上海万江环境科技有限公司"
              className={`w-[clamp(2rem,4vw,2.5rem)] h-[clamp(2rem,4vw,2.5rem)] object-contain transition-all duration-300 ${isScrolled ? 'filter-none' : 'brightness-0 invert'}`}
            />
            <span className={`text-[clamp(1rem,2vw,1.25rem)] font-bold transition-colors duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'}`} style={{ fontFamily: 'Pacifico, serif' }}>
              上海万江环境科技有限公司
            </span>
          </div>

          {/* 桌面端导航 */}
          <nav className="hidden md:flex items-center space-x-[clamp(1rem,2vw,2rem)]">
            {[
              { label: '首页', section: 'hero' },
              { label: '关于我们', section: 'about' },
              { label: '成功案例', section: 'cases' },
              { label: '新闻动态', section: 'news' }
            ].map((item) => (
              <button
                type="button"
                key={item.section}
                onClick={() => scrollToSection(item.section)}
                className={`transition-colors whitespace-nowrap cursor-pointer text-[clamp(0.8125rem,1.2vw,0.875rem)] font-bold ${activeSection === item.section 
                  ? (isScrolled ? 'text-[#4677e0]' : 'text-white font-bold') 
                  : (isScrolled ? 'text-gray-800 hover:text-[#4677e0]' : 'text-white/80 hover:text-white')}`}
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => scrollToSection('contact')}
              className={`px-[clamp(1rem,2vw,1.5rem)] py-[clamp(0.375rem,0.75vw,0.5rem)] rounded-lg transition-all whitespace-nowrap cursor-pointer text-[clamp(0.8125rem,1.2vw,0.875rem)] font-bold ${activeSection === 'contact' 
                ? (isScrolled ? 'bg-[#4677e0] text-white font-bold shadow-md' : 'bg-white text-[#4677e0] font-bold') 
                : (isScrolled ? 'bg-[#4677e0] text-white hover:bg-[#3a5cc9]' : 'bg-white text-[#4677e0] hover:bg-white/90')}`}
            >
              联系我们
            </button>
          </nav>

          {/* 移动端菜单按钮 */}
          <button
            ref={menuButtonRef}
            type="button"
            onClick={toggleMobileMenu}
            className="md:hidden p-[clamp(0.25rem,0.5vw,0.5rem)] cursor-pointer"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-[clamp(1.25rem,2.5vw,1.5rem)] transition-colors duration-300 ${isScrolled ? 'text-gray-800' : 'text-white'}`}></i>
          </button>
        </div>

        {/* 移动端菜单 */}
        <div 
          ref={menuRef}
          className={`md:hidden overflow-hidden transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          style={{
            transition: menuTransition,
            maxHeight: isMobileMenuOpen ? '500px' : '0',
            willChange: 'max-height, opacity',
          }}
        >
          <nav className={`pt-[clamp(0.5rem,1vw,0.75rem)] pb-[clamp(0.25rem,0.5vw,0.5rem)] space-y-[clamp(0.25rem,0.5vw,0.5rem)] border-t ${isScrolled ? 'border-gray-200' : 'border-white/20'}`}>
            {[
              { label: '首页', section: 'hero' },
              { label: '关于我们', section: 'about' },
              { label: '成功案例', section: 'cases' },
              { label: '新闻动态', section: 'news' }
            ].map((item) => (
              <button
                type="button"
                key={item.section}
                onClick={() => scrollToSection(item.section)}
                className={`block w-full text-left px-[clamp(0.75rem,1.5vw,1rem)] py-[clamp(0.5rem,1vw,0.75rem)] rounded-lg transition-colors whitespace-nowrap cursor-pointer text-[clamp(0.8125rem,1.2vw,0.875rem)] font-bold ${activeSection === item.section 
                  ? (isScrolled ? 'bg-gray-100 text-[#4677e0] font-bold' : 'bg-white/20 text-white font-bold') 
                  : (isScrolled ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/10')}`}
              >
                {item.label}
              </button>
            ))}
            <button
                type="button"
                onClick={() => scrollToSection('contact')}
                className={`block w-full text-left px-[clamp(0.75rem,1.5vw,1rem)] py-[clamp(0.5rem,1vw,0.75rem)] rounded-lg transition-colors whitespace-nowrap cursor-pointer text-[clamp(0.8125rem,1.2vw,0.875rem)] font-bold ${activeSection === 'contact' 
                  ? (isScrolled ? 'bg-gray-100 text-[#4677e0] font-bold' : 'bg-white/20 text-white font-bold') 
                  : (isScrolled ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/10')}`}
              >
                联系我们
              </button>
          </nav>
        </div>
      </div>
    </header>
  );
}